import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { Box, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ObjectInfoCard from "../../components/ObjectInfoCard.jsx";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import BranchSelector, {
  getBranchName,
} from "../../components/BranchSelector.jsx";
import NewDamageClaimDialog from "../../components/NewDamageClaimDialog.jsx";
import {
  colorsFoundation,
  borderRadius,
} from "@accredo/fe-shared/theme/index.js";
import ClaimForm from "../../components/claim/ClaimForm.jsx";
import { FORM_DEF_MAP, DEFAULT_FORM_DEF } from "formdefs/formDefMap.js";
import AccessDeniedNotice from "@accredo/fe-shared/components/errors/AccessDeniedNotice.jsx";

const breadcrumbs = [
  { name: "Home", color: "secondary", path: "/home" },
  { name: "Schaden melden", color: "primary", route: "/schaden" },
];

export default function ClaimCreatePage(props) {
  const { setBreadcrumbs } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSparte, setSelectedSparte] = useState(null);
  const [objectDialogOpen, setObjectDialogOpen] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const { vertrag_id, objekt_id } = useParams();
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (setBreadcrumbs) {
      setBreadcrumbs(breadcrumbs);
    }
  }, [setBreadcrumbs]);

  useEffect(() => {
    const getData = async () => {
      const endpoint = "data/schadenanlage/" + vertrag_id + "/" + objekt_id;
      await backendGet(endpoint, {
        setIsLoading,
        onError: setLoadError,
        setResult: (data) => {
          setData(data);
          setIsLoading(false);
        },
      });
    };
    getData();
  }, []);

  const [formDefinition, setFormDefinition] = useState(DEFAULT_FORM_DEF);
  // Whether `formDefinition` has settled on the sparte's real definition, as
  // opposed to the DEFAULT_FORM_DEF placeholder it starts as. ClaimForm only
  // mounts once this is true, showing a Spinner meanwhile rather than briefly
  // rendering the placeholder's empty stepper.
  const [formDefinitionReady, setFormDefinitionReady] = useState(false);

  useEffect(() => {
    if (!selectedSparte) return;
    setFormDefinitionReady(false);
    backendGet(`data/sparten/${selectedSparte}`).then((res) => {
      const currentDefinition = res?.data?.claim_form?.current_definition;
      // definition_file_name stays authoritative while set (rollout switch:
      // nulling it on a ClaimFormDefinition opts that sparte into the
      // DB-backed definition, already materialized server-side).
      setFormDefinition(
        FORM_DEF_MAP[currentDefinition?.definition_file_name] ??
          currentDefinition?.definition ??
          DEFAULT_FORM_DEF
      );
      setFormDefinitionReady(true);
    });
  }, [selectedSparte]);

  let bezeichnung = null;
  let sparteObj = null;
  if (data?.objekt?.sparten && selectedSparte) {
    const sparte = data.objekt.sparten.find(
      (sparte) => sparte.sparte_id === selectedSparte
    );
    const obj = data?.objekt;
    if (obj.bezeichnung) bezeichnung = obj?.bezeichnung;
    if (obj.strasse) {
      bezeichnung = obj.strasse;
      if (obj.hausnr) bezeichnung += " " + obj.hausnr;
      if (obj.plz) bezeichnung += ", " + obj.plz;
      if (obj.ort) bezeichnung += " " + obj.ort;
    }
    sparteObj = sparte;
  }

  const handleEditSparte = () => {
    setSelectedSparte(null);
  };

  // Get display name for the current object
  const getObjectAndContractDisplayName = (object, contract) => {
    if (!object && !contract) {
      return " - unbekanntes Objekt / Vertrag -";
    }

    let object_name = "";

    if (object.bezeichnung) {
      object_name = object.bezeichnung;
    } else if (object.strasse) {
      object_name = object.strasse;
      if (object.hausnr) {
        object_name += " " + object.hausnr;
      }
    }

    let contract_name = contract.vertragsnummer;
    if (contract?.gesellschaft_name) {
      contract_name += ` (${contract.gesellschaft_name})`;
    }

    return object_name + " - " + contract_name;
  };

  // Handle object change from dialog
  const handleObjectChange = (object) => {
    navigate(`/schaeden/neu/${object.vertrag_id}/${object.id}`);
  };

  if (!isLoading && loadError) {
    return (
      <AccessDeniedNotice
        status={loadError.response?.status}
        substitution={loadError.response?.data?.substitution}
      />
    );
  }

  return (
    <>
      {isLoading || userLoading || !data ? (
        <Spinner />
      ) : (
        <Box>
          {!selectedSparte ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h1">Neuer Schaden</Typography>
              </Box>

              {/* Object/Contract Selector Input */}
              <Box sx={{ mb: 3 }}>
                <Box
                  onClick={() => setObjectDialogOpen(true)}
                  sx={{
                    position: "relative",
                    border: `1px solid ${colorsFoundation.neutral[300]}`,
                    borderRadius: borderRadius.xs,
                    backgroundColor: colorsFoundation.neutral.white,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: colorsFoundation.neutral[400],
                    },
                  }}
                >
                  {/* Floating label */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "12px",
                      backgroundColor: colorsFoundation.neutral.white,
                      px: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: colorsFoundation.neutral[700],
                      }}
                    >
                      Objekt / Vertrag
                    </Typography>
                  </Box>

                  {/* Input content */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "56px",
                    }}
                  >
                    <Box sx={{ px: 1.5, py: 0.5, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: colorsFoundation.neutral.softblack,
                        }}
                      >
                        {getObjectAndContractDisplayName(
                          data.objekt,
                          data.vertrag
                        )}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "56px",
                        height: "100%",
                      }}
                    >
                      <SearchOutlinedIcon
                        sx={{ color: colorsFoundation.neutral[700] }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <BranchSelector
                data={data.objekt.sparten}
                selectedSparte={selectedSparte}
                setSelectedSparte={setSelectedSparte}
              />
            </Box>
          ) : (
            <Box>
              <Box>
                <Typography variant="h1" sx={{ mb: 4 }}>
                  Schadenformular
                </Typography>
              </Box>
              {data?.objekt && (
                <ObjectInfoCard
                  object={data.objekt}
                  contract={data.vertrag}
                  branch={getBranchName(sparteObj)}
                  status={{ label: "Entwurf", value: "draft" }}
                  onEditBranch={handleEditSparte}
                />
              )}
              {formDefinitionReady ? (
                <ClaimForm
                  hideTitle={true}
                  model={{
                    vertragsnummer: data.vertrag.vertragsnummer,
                    gesellschaft: data.vertrag.gesellschaft_name,
                    vertrag_id: data.vertrag.id,
                    objekt_id: objekt_id,
                    sparte_id: selectedSparte,
                    vorab_pruefung: false,
                    bezeichnung: bezeichnung,
                    user: user,
                  }}
                  useV2Layout={true}
                  formDefinition={formDefinition}
                  objektSparten={data.objekt.sparten}
                />
              ) : (
                <Spinner />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Object Selection Dialog */}
      <NewDamageClaimDialog
        open={objectDialogOpen}
        onClose={() => setObjectDialogOpen(false)}
        onSelect={handleObjectChange}
        title="Objekt / Vertrag ändern"
      />
    </>
  );
}
